import React, { useEffect } from 'react'
import { ScrollView } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { Button } from '@zet/design-system/button'
import { Input } from '@zet/design-system/input'
import { Stack } from '@zet/design-system/layout'
import { B1, B2, B5 } from '@zet/design-system/typography'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSaveAdvisorDetails } from '../../api/accountDetailsBridge/hooks'
import useStore from '../../store'
import { EMAIL_REGEX, NAME_REGEX } from '../../utils/regularExpression'
import {
  EMAIL_FIELD_VALIDATION,
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '../../utils/validationMessage'

const validationSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
  email: z.preprocess(
    (arg) => (arg === '' ? undefined : arg),
    z
      .string()
      .trim()
      .regex(new RegExp(EMAIL_REGEX), EMAIL_FIELD_VALIDATION)
      .optional()
  ),
})

const EditDetails = () => {
  const { data } = useUserContext()

  type ValidationSchema = z.infer<typeof validationSchema>
  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: { email: data?.emailAddress, fullName: data?.name },
  })

  const navigation = useNavigation()

  useEffect(() => {
    reset({ email: data?.emailAddress, fullName: data?.name })
  }, [data])

  const { selectedDsaId } = useStore()
  const { mutate, isLoading } = useSaveAdvisorDetails()

  const queryClient = useQueryClient()

  const handleSave = () => {
    mutate(
      {
        name: getValues('fullName'),
        emailAddress: getValues('email'),
      },
      {
        onSuccess: (data) => {
          if (data?.data === 'saved') {
            queryClient.invalidateQueries({ queryKey: ['userContext'] })
            navigation.goBack()
          }
        },
      }
    )
  }

  return (
    <KeyBoardWrapper
      className=" flex-1 bg-white"
      BottomSheet={
        <Stack className=" border-t-grays-30 border-t px-3 py-[12]">
          <Button
            size="lg"
            className="w-full"
            disabled={!isValid || !isDirty}
            loading={isLoading}
            onPress={handleSave}
          >
            {i18n.t('editProfile.save')}
          </Button>
        </Stack>
      }
    >
      <ScrollView>
        <Stack className="mx-3 mb-1 mt-3">
          <B2 className="text-grays-80  ml-1">
            {i18n.t('editProfile.fullName')}
            <B1 className="text-error-100 ml-1">*</B1>
          </B2>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                placeholder="Eg: Ankush Singh"
                onChangeText={(value) => {
                  onChange(value)
                }}
                value={value}
                type={`${errors.fullName?.message ? 'error' : 'primary'}`}
                className=" mt-1 "
                onBlur={() => {
                  onBlur()
                }}
              />
            )}
            name="fullName"
          />
          {errors.fullName?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.fullName?.message}</B5>
            </Stack>
          ) : null}
        </Stack>

        <Stack className="mx-3 mb-1 mt-3">
          <B2 className="text-grays-80 ml-1">
            {i18n.t('userDetails.yourEmail')}
          </B2>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                placeholder="Eg: ankush.singh@gmail.com"
                onChangeText={(value) => {
                  onChange(value)
                }}
                value={value}
                type={`${errors.email?.message ? 'error' : 'primary'}`}
                className=" mt-1 "
                onBlur={() => {
                  onBlur()
                }}
              />
            )}
            name="email"
          />
          {errors.email?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.email?.message}</B5>
            </Stack>
          ) : null}
        </Stack>
        <Stack className="mx-3 mb-1 mt-3">
          <B2 className="text-grays-50 ml-1">
            {i18n.t('accountDetails.registeredNum')}
          </B2>
          <Stack className="bg-grays-20 rounded-md px-3 py-3">
            <B2 className=" text-grays-50">+91-{data?.phoneNumber}</B2>
          </Stack>
        </Stack>
      </ScrollView>
    </KeyBoardWrapper>
  )
}

export default EditDetails
